import { Badge } from '@/components/ui';
import {
  getRecommendationComponentLabel,
  normalizeRecommendationEntries,
  ScenarioSectionContent,
} from '@/features/okdoc/components/RecommendationSectionContent';
import { getOkdocCTAActionLabel, getOkdocInteractionIntentLabel } from '@/features/okdoc/registry/ctaRegistry';
import type { OkdocScenarioProfile } from '@/features/okdoc/types';

interface RecommendationsScenarioPreviewProps {
  profile?: OkdocScenarioProfile;
}

// 19.03.26 2.: компактный preview scenario profile в админке.
export function RecommendationsScenarioPreview({ profile }: RecommendationsScenarioPreviewProps) {
  if (!profile) {
    return null;
  }

  return (
    <div className="space-y-4 rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="info">{profile.kind || 'scenario'}</Badge>
          {profile.badge && <Badge variant="secondary">{profile.badge}</Badge>}
        </div>
        <div className="text-lg font-semibold text-neutral-900">
          {profile.title || 'Scenario preview'}
        </div>
        {(profile.subtitle || profile.description) && (
          <div className="text-sm leading-6 text-neutral-600">
            {profile.subtitle ? `${profile.subtitle}. ` : ''}
            {profile.description}
          </div>
        )}
      </div>

      {profile.sections && profile.sections.length > 0 && (
        <div className="space-y-3">
          <div className="text-sm font-medium text-neutral-900">Секции landing</div>
          {profile.sections.map((section, index) => (
            <div key={section.id || `section-${index}`} className="rounded-xl border border-white bg-white p-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-medium text-neutral-900">{section.title || `Секция ${index + 1}`}</div>
                <Badge variant="secondary">{getRecommendationComponentLabel(section.component)}</Badge>
                {section.image?.url && <Badge variant="secondary">Изображение</Badge>}
                <Badge variant="secondary">{normalizeRecommendationEntries(section).length} элементов</Badge>
              </div>
              {section.description && (
                <div className="mt-1 text-sm text-neutral-600">{section.description}</div>
              )}
              <ScenarioSectionContent section={section} mode="preview" />
            </div>
          ))}
        </div>
      )}

      <div className="space-y-3">
        <div className="text-sm font-medium text-neutral-900">CTA</div>
        {profile.ctas && profile.ctas.length > 0 ? (
          profile.ctas.map((cta, index) => (
            <div key={cta.id || cta.target || `cta-${index}`} className="rounded-xl border border-white bg-white p-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-medium text-neutral-900">{cta.label || `CTA ${index + 1}`}</div>
                {cta.kind && <Badge variant="secondary">{cta.kind}</Badge>}
                {cta.action_kind && <Badge variant="secondary">{getOkdocCTAActionLabel(cta.action_kind)}</Badge>}
                {cta.interaction_intent && <Badge variant="secondary">{getOkdocInteractionIntentLabel(cta.interaction_intent)}</Badge>}
              </div>
              <div className="mt-1 text-xs text-neutral-500">
                target: {cta.target || cta.id || '—'}
              </div>
              {cta.url && (
                <div className="mt-2 break-all text-sm text-primary-700">{cta.url}</div>
              )}
            </div>
          ))
        ) : (
          <div className="rounded-xl border border-dashed border-neutral-300 bg-white p-3 text-sm text-neutral-500">
            CTA пока не настроены.
          </div>
        )}
      </div>
    </div>
  );
}
